"use client";
import { useState, useRef, useEffect } from "react";
import DesktopNav from "./DesktopNav";
import MobileNav from "./MobileNav";
import { Nav } from "@/types/Navigation";

import { ActiveBanner } from "./types";
import { EventCacheType, resetEventCache } from "@/utils/tagular/helpers";
import { ProcessedCourse } from "@/common/course/types";
import { Program } from "@/common/program/types";
import { useAuthBaseURL } from "@/hooks/useAuthBaseURL";
import Modal from "./Modal";
import { WpHeaderNavResponse } from "@/lib/wordpress/getMenusFromWP";

type Props = {
  navigation: Nav;
  activeBanner?: ActiveBanner;
  course?: ProcessedCourse;
  program?: Program;
  headerNavData?: WpHeaderNavResponse;
  bannerContainerClassname?: string;
};

export default function Navigation({
  navigation,
  activeBanner,
  course,
  program,
  headerNavData,
  bannerContainerClassname = "",
}: Props) {
  const authBaseUrl = useAuthBaseURL();
  const [openNav, setOpenNavState] = useState(false);
  const desktopNavButtonRef = useRef<HTMLButtonElement>(null);
  const mobileNavButtonRef = useRef<HTMLButtonElement>(null);

  navigation.header.cta.register.url = new URL(
    "register",
    authBaseUrl,
  ).toString();
  navigation.header.cta.signIn.url = new URL("login", authBaseUrl).toString();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setOpenNav = (value: boolean) => {
    if (value) {
      resetEventCache(EventCacheType.MegaNav);
    }
    setOpenNavState(value);
  };

  useEffect(() => {
    if (!openNav) return;

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setOpenNav(false);

        // Focus the navigation button after closing
        setTimeout(() => {
          const isMobile = window.innerWidth < 768; // md breakpoint
          if (isMobile) {
            mobileNavButtonRef?.current?.focus();
          } else {
            desktopNavButtonRef?.current?.focus();
          }
        }, 0);
      }
    };

    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [openNav, setOpenNav]);
  return (
    <div className="relative">
      <header className="contents mx-auto h-[76px] pointer-events-auto">
        <DesktopNav
          course={course}
          program={program}
          navigation={navigation}
          openNav={openNav}
          setOpenNav={setOpenNav}
          activeBanner={activeBanner}
          navButtonRef={desktopNavButtonRef}
          bannerContainerClassname={bannerContainerClassname}
        />
        <MobileNav
          navigation={navigation}
          openNav={openNav}
          setOpenNav={setOpenNav}
          activeBanner={activeBanner}
          navButtonRef={mobileNavButtonRef}
          bannerContainerClassname={bannerContainerClassname}
        />
      </header>
      <Modal
        open={openNav}
        headerNavData={headerNavData}
        onOpenChange={setOpenNav}
      />
    </div>
  );
}
